import { useRef, useCallback, useEffect } from 'react';

import { businessKey } from 'queryKeys';

import { useSilentInvalidateQueries, useQueryClient } from '@gogame/query';
import { useSocket } from '@gogame/sockets';

import { EVENT_REFETCH } from './constants';

export const useSocketQueryRefetch = () => {
  const socket = useSocket();
  const invalidateQueries = useSilentInvalidateQueries();
  const queryClient = useQueryClient();

  const missedEventsRef = useRef([]);

  const handleRefetchGrid = useCallback(
    async ([scope, id]) => {
      const queryKeyGrid = businessKey.query(scope).grids()();

      if (id) {
        const cacheData = queryClient.getQueryData(queryKeyGrid, {
          exact: false,
        });

        if (cacheData?.rows?.some((row) => row?.id === id)) {
          await invalidateQueries(queryKeyGrid);
        }
      } else {
        await invalidateQueries(queryKeyGrid);
      }
    },
    [invalidateQueries]
  );

  const handleRefetchDetails = useCallback(
    async ([scope, id]) => {
      const queryKeyDetails = businessKey.query(scope).details()(id);
      await invalidateQueries(queryKeyDetails);
    },
    [invalidateQueries]
  );

  const handleRefetchEvent = useCallback(
    ([scope, id]) => {
      if (!document.hidden) {
        handleRefetchGrid([scope, id]);
        handleRefetchDetails([scope, id]);
      } else {
        missedEventsRef.current.push([scope, id]);
      }
    },
    [handleRefetchGrid, handleRefetchDetails]
  );

  const handleVisibilityChange = useCallback(() => {
    setTimeout(() => {
      if (!document.hidden) {
        missedEventsRef.current.forEach(([scope, id]) => {
          handleRefetchDetails([scope, id]);
          handleRefetchGrid([scope, id]);
        });
        missedEventsRef.current = [];
      }
    }, 1000);
  }, []);

  useEffect(() => {
    socket.on(EVENT_REFETCH, handleRefetchEvent);
    return () => {
      socket.off(EVENT_REFETCH, handleRefetchEvent);
    };
  }, [socket, handleRefetchEvent]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);
};
